import React, { useState, useEffect, useCallback } from 'react';
import { View, ActivityIndicator, RefreshControl, ScrollView } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

// React Native Reusables Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Services
import { creditsAPI } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';

// Credit Components
import { CreditPurchaseModal } from '@/components/credits';
import { brand } from '../../design-system/theme/tokens';

// Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
};

interface SchoolCreditStatus {
  school_id: number;
  paid_seat_capacity?: number;
  analysis_credits_per_seat?: number;
  subscription_credit_allowance?: number;
  subscription_credits: {
    total: number;
    used: number;
    remaining: number;
  };
  purchased_credits: number;
  total_available: number;
  period_start: string | null;
  period_end: string | null;
}

interface StudentUsage {
  user_id: number;
  full_name: string;
  email: string;
  credits_used: number;
}

interface SchoolCreditDashboardProps {
  schoolId: number;
  compact?: boolean; // For embedding in overview tab
}

export function SchoolCreditDashboard({ schoolId, compact = false }: SchoolCreditDashboardProps) {
  const { t } = useTranslation();
  const [creditStatus, setCreditStatus] = useState<SchoolCreditStatus | null>(null);
  const [studentUsage, setStudentUsage] = useState<StudentUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const loadCreditData = useCallback(async () => {
    try {
      setError(null);
      const [statusRes, usageRes] = await Promise.all([
        creditsAPI.getSchoolStatus(schoolId),
        creditsAPI.getSchoolUsage(schoolId),
      ]);
      setCreditStatus(statusRes);
      setStudentUsage(usageRes.usage || []);
    } catch (err: any) {
      console.error('Failed to load school credit data:', err);
      setError(err?.response?.data?.detail || t('common.loadError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [schoolId, t]);

  useEffect(() => {
    loadCreditData();
  }, [loadCreditData]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadCreditData();
  }, [loadCreditData]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate usage percentage
  const usagePercentage = creditStatus?.subscription_credits
    ? Math.round((creditStatus.subscription_credits.used / Math.max(creditStatus.subscription_credits.total, 1)) * 100)
    : 0;

  const isLowCredit = usagePercentage >= 80;
  const isCritical = usagePercentage >= 95;

  if (loading) {
    return (
      <View className="items-center justify-center py-8">
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text className="mt-2 text-muted-foreground">{t('common.loading')}</Text>
      </View>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-4 items-center">
          <MaterialIcons name="error-outline" size={32} color={COLORS.error} />
          <Text className="text-red-700 mt-2">{error}</Text>
          <Button variant="secondary" size="sm" onPress={loadCreditData} className="mt-3">
            <Text>{t('common.retry')}</Text>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!creditStatus) return null;

  // Compact view for embedding in overview tab
  if (compact) {
    return (
      <>
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <View className="flex-row items-center justify-between">
              <View className="flex-row items-center">
                <MaterialIcons name="stars" size={20} color={COLORS.primary} />
                <Text className="text-lg font-semibold text-foreground ml-2">
                  {t('schoolCredits.title')}
                </Text>
              </View>
              {isLowCredit && (
                <View className={`px-2 py-1 rounded ${isCritical ? 'bg-red-100' : 'bg-orange-100'}`}>
                  <Text className={`text-xs font-medium ${isCritical ? 'text-red-700' : 'text-orange-700'}`}>
                    {isCritical ? t('schoolCredits.critical') : t('schoolCredits.low')}
                  </Text>
                </View>
              )}
            </View>
          </CardHeader>
          <CardContent className="pt-2">
            {/* Credit Summary */}
            <View className="flex-row items-center justify-between mb-3">
              <View>
                <Text className="text-3xl font-bold text-foreground">
                  {creditStatus.total_available}
                </Text>
                <Text className="text-sm text-muted-foreground">{t('schoolCredits.available')}</Text>
              </View>
              <Button variant="default" size="sm" onPress={() => setShowPurchaseModal(true)}>
                <MaterialIcons name="add" size={16} color="white" />
                <Text className="text-primary-foreground font-medium ml-1">{t('schoolCredits.buyMore')}</Text>
              </Button>
            </View>

            {/* Usage Bar */}
            <View className="mb-3">
              <View className="flex-row justify-between mb-1">
                <Text className="text-xs text-muted-foreground">
                  {t('schoolCredits.poolUsage')}
                </Text>
                <Text className="text-xs text-muted-foreground">
                  {creditStatus.subscription_credits.used}/{creditStatus.subscription_credits.total}
                </Text>
              </View>
              <View className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <View
                  className={`h-full rounded-full ${
                    isCritical ? 'bg-red-500' : isLowCredit ? 'bg-orange-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                />
              </View>
            </View>

            {/* Period Info */}
            <View className="flex-row justify-between">
              <Text className="text-xs text-muted-foreground">
                {t('schoolCredits.periodEnds')}: {formatDate(creditStatus.period_end)}
              </Text>
              <Text className="text-xs text-muted-foreground">
                +{creditStatus.purchased_credits} {t('schoolCredits.purchased')}
              </Text>
            </View>
          </CardContent>
        </Card>

        <CreditPurchaseModal
          visible={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          onPurchaseComplete={() => {
            setShowPurchaseModal(false);
            loadCreditData();
          }}
          schoolId={schoolId}
        />
      </>
    );
  }

  // Full view with student usage table
  return (
    <ScrollView
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      {/* Credit Overview Card */}
      <Card className="mb-4">
        <CardHeader>
          <View className="flex-row items-center justify-between">
            <Text className="text-lg font-semibold text-foreground">{t('schoolCredits.overview')}</Text>
            {isLowCredit && (
              <View className={`px-2 py-1 rounded ${isCritical ? 'bg-red-100' : 'bg-orange-100'}`}>
                <Text className={`text-xs font-medium ${isCritical ? 'text-red-700' : 'text-orange-700'}`}>
                  {isCritical ? t('schoolCredits.critical') : t('schoolCredits.lowWarning')}
                </Text>
              </View>
            )}
          </View>
        </CardHeader>
        <CardContent>
          {/* Main Credit Display */}
          <View className="bg-blue-50 rounded-xl p-4 mb-4">
            <View className="flex-row items-center justify-between">
              <View className="flex-row items-center">
                <MaterialIcons name="stars" size={32} color={COLORS.primary} />
                <View className="ml-3">
                  <Text className="text-sm text-muted-foreground">{t('schoolCredits.totalAvailable')}</Text>
                  <Text className="text-3xl font-bold text-foreground">
                    {creditStatus.total_available}
                  </Text>
                </View>
              </View>
              <Button variant="default" onPress={() => setShowPurchaseModal(true)}>
                <MaterialIcons name="add-circle-outline" size={18} color="white" />
                <Text className="text-primary-foreground font-semibold ml-2">{t('schoolCredits.buyMore')}</Text>
              </Button>
            </View>
          </View>

          {/* Usage Progress Bar */}
          <View className="mb-4">
            <View className="flex-row justify-between mb-2">
              <Text className="text-sm font-medium text-foreground">{t('schoolCredits.subscriptionPool')}</Text>
              <Text className="text-sm text-muted-foreground">
                {creditStatus.subscription_credits.used} / {creditStatus.subscription_credits.total} {t('schoolCredits.used')}
              </Text>
            </View>
            <View className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <View
                className={`h-full rounded-full ${
                  isCritical ? 'bg-red-500' : isLowCredit ? 'bg-orange-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </View>
            <Text className="text-xs text-muted-foreground mt-1 text-right">{usagePercentage}%</Text>
          </View>

          {/* Credit Breakdown */}
          <View className="gap-3">
            {typeof creditStatus.paid_seat_capacity === 'number' && (
              <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
                <View className="flex-row items-center">
                  <MaterialIcons name="event-seat" size={18} color="#6B7280" />
                  <Text className="text-muted-foreground ml-2">{t('schoolCredits.paidSeatCapacity')}:</Text>
                </View>
                <Text className="text-foreground font-medium">{creditStatus.paid_seat_capacity}</Text>
              </View>
            )}

            {typeof creditStatus.analysis_credits_per_seat === 'number' && (
              <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
                <View className="flex-row items-center">
                  <MaterialIcons name="analytics" size={18} color="#6B7280" />
                  <Text className="text-muted-foreground ml-2">{t('schoolCredits.creditsPerSeat')}:</Text>
                </View>
                <Text className="text-foreground font-medium">{creditStatus.analysis_credits_per_seat}</Text>
              </View>
            )}

            <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
              <View className="flex-row items-center">
                <MaterialIcons name="autorenew" size={18} color="#6B7280" />
                <Text className="text-muted-foreground ml-2">{t('schoolCredits.subscriptionRemaining')}:</Text>
              </View>
              <Text className="text-foreground font-medium">{creditStatus.subscription_credits.remaining}</Text>
            </View>

            <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
              <View className="flex-row items-center">
                <MaterialIcons name="shopping-cart" size={18} color="#6B7280" />
                <Text className="text-muted-foreground ml-2">{t('schoolCredits.purchasedCredits')}:</Text>
              </View>
              <Text className="text-foreground font-medium">{creditStatus.purchased_credits}</Text>
            </View>

            <View className="flex-row justify-between items-center py-2 border-b border-gray-100">
              <View className="flex-row items-center">
                <MaterialIcons name="date-range" size={18} color="#6B7280" />
                <Text className="text-muted-foreground ml-2">{t('schoolCredits.billingPeriod')}:</Text>
              </View>
              <Text className="text-foreground font-medium">
                {formatDate(creditStatus.period_start)} - {formatDate(creditStatus.period_end)}
              </Text>
            </View>
          </View>

          {/* Info Note */}
          <View className="bg-gray-50 rounded-lg p-3 mt-4">
            <Text className="text-xs text-muted-foreground text-center">
              {t('schoolCredits.poolNote')}
            </Text>
          </View>
        </CardContent>
      </Card>

      {/* Student Usage Table */}
      <Card className="mb-4">
        <CardHeader>
          <Text className="text-lg font-semibold text-foreground">{t('schoolCredits.usageByStudent')}</Text>
        </CardHeader>
        <CardContent>
          {studentUsage.length === 0 ? (
            <View className="items-center py-6">
              <MaterialIcons name="people-outline" size={40} color="#9CA3AF" />
              <Text className="text-muted-foreground mt-2">{t('schoolCredits.noUsageYet')}</Text>
            </View>
          ) : (
            <View>
              {/* Table Header */}
              <View className="flex-row py-2 border-b border-gray-200">
                <Text className="flex-1 text-xs font-semibold text-muted-foreground uppercase">
                  {t('schoolCredits.student')}
                </Text>
                <Text className="w-20 text-xs font-semibold text-muted-foreground uppercase text-right">
                  {t('schoolCredits.credits')}
                </Text>
              </View>

              {/* Table Rows */}
              {studentUsage.map((student, index) => (
                <View
                  key={student.user_id}
                  className={`flex-row items-center py-3 ${
                    index < studentUsage.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <View className="flex-1">
                    <Text className="text-sm font-medium text-foreground">
                      {student.full_name || student.email}
                    </Text>
                    {student.full_name && (
                      <Text className="text-xs text-muted-foreground">{student.email}</Text>
                    )}
                  </View>
                  <View className="w-20 items-end">
                    <View className="bg-blue-100 px-2 py-1 rounded">
                      <Text className="text-sm font-semibold text-blue-700">{student.credits_used}</Text>
                    </View>
                  </View>
                </View>
              ))}

              {/* Total Row */}
              <View className="flex-row items-center py-3 mt-2 border-t border-gray-200">
                <Text className="flex-1 text-sm font-semibold text-foreground">
                  {t('schoolCredits.totalUsed')}
                </Text>
                <View className="w-20 items-end">
                  <Text className="text-sm font-bold text-foreground">
                    {studentUsage.reduce((sum, s) => sum + s.credits_used, 0)}
                  </Text>
                </View>
              </View>
            </View>
          )}
        </CardContent>
      </Card>

      <CreditPurchaseModal
        visible={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onPurchaseComplete={() => {
          setShowPurchaseModal(false);
          loadCreditData();
        }}
        schoolId={schoolId}
      />
    </ScrollView>
  );
}

export default SchoolCreditDashboard;
